import { API_ENDPOINT } from './utils';
import getData from './dataService';

// ── Coordinate reversal (GeoJSON [lng,lat] → Leaflet [lat,lng]) ─────────────
const _swap = ([lng, lat]) => [lat, lng];
const _reversePolygon = (coords) => coords.map(ring => ring.map(_swap));
const _reverseMultiPolygon = (coords) =>
  coords.map(poly =>
    // The backend occasionally sends a Polygon geometry tagged as MultiPolygon
    poly.map(ring => (typeof ring[0] === 'number' ? _swap(ring) : ring.map(_swap)))
  );

function _reverseGeoJson(geoJson) {
  geoJson.features.forEach(feature => {
    const { type, coordinates } = feature.geometry;
    if (type === 'Polygon') {
      feature.geometry.coordinates = _reversePolygon(coordinates);
    } else if (type === 'MultiPolygon') {
      feature.geometry.coordinates = _reverseMultiPolygon(coordinates);
    }
  });
}

// ── Region-2 result transformer ──────────────────────────────────────────────
function _transformRegion2Result(isLadestation, result) {
  if (!isLadestation) return result;
  const features = result.map(({ long, lat }) => ({
    geometry: { type: 'Point', coordinates: [long, lat] }
  }));
  return { data: [{ features }] };
}

class MapApi {
  constructor(pageData) {
    if (!pageData?.meta) {
      throw new Error('MapApi expected a pageData parameter with meta, but received: ' + pageData);
    }

    this.api = pageData.meta.api ?? {};
    this.isLadestation = pageData.meta.data?.markerIcon === 'ladestation';
  }

  async fetchStates(signal) {
    const requestUrl = `${API_ENDPOINT()}/geo/states`;
    const result = await getData(requestUrl, { key: requestUrl, signal });

    _reverseGeoJson(result.data[0]);

    return result.data[0];
  }

  async fetchLandkreise(signal) {
    const requestUrl = `${API_ENDPOINT()}/geo/districts`;
    const result = await getData(requestUrl, { key: requestUrl, signal });

    _reverseGeoJson(result.data[0]);

    return result.data[0];
  }

  async fetchRegion2Geos(signal) {
    const requestUrl = this._toFullUrl(this.api.geosRegion2);
    if (!requestUrl) return null;

    const result = await getData(requestUrl, { key: requestUrl, signal });

    return _transformRegion2Result(this.isLadestation, result);
  }

  async fetchRanksForStates(year, signal) {
    let requestUrl = this._toFullUrl(this.api.ranksStates);
    if (!requestUrl) return null;

    if (year) {
      requestUrl = `${requestUrl}/${year}`;
    }

    let result;

    try {
      result = await getData(requestUrl, { key: requestUrl, signal });
    } catch {
      return null;
    }

    return result;
  }

  async fetchRanksForLandkreise(year, signal) {
    let requestUrl = this._toFullUrl(this.api.ranksLandkreise);
    if (!requestUrl) return null;

    if (year) {
      requestUrl = `${requestUrl}/${year}`;
    }

    let result;
    try {
      result = await getData(requestUrl, { key: requestUrl, signal });
    } catch {
      return null;
    }

    return result;
  }

  async fetchTooltip(region, ags, year, category) {
    let requestUrl = this._toFullUrl(this.api.tooltip);
    if (!requestUrl) return null;

    requestUrl = `${requestUrl}/${region}/${ags}`;

    if (category) {
      requestUrl += `/${category}`;
    }

    if (year) {
      requestUrl += `/${year}`;
    }

    const result = await getData(requestUrl, { key: requestUrl });

    return result;
  }

  _toFullUrl(route) {
    if (!route) return undefined;
    if (route.startsWith('/data/')) {
      return route;
    }

    return `${API_ENDPOINT()}${route}`;
  }
}

export default MapApi;
